/* global Sanki, gsap, ScrollTrigger */
/**
 * Sanki — animations.js
 * Scroll-triggered reveals, hero parallax, and header scroll state.
 * Uses GSAP + ScrollTrigger if available (loaded from cdnjs in theme.liquid),
 * otherwise gracefully falls back to IntersectionObserver + CSS transitions.
 */
(function (global) {
  'use strict';

  if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document.querySelectorAll('[data-sk-reveal]').forEach((el) => el.classList.add('is-visible'));
    return;
  }

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function init() {
    const hasGSAP = typeof gsap !== 'undefined';
    const hasST = typeof ScrollTrigger !== 'undefined';
    if (hasGSAP && hasST) gsap.registerPlugin(ScrollTrigger);

    // Reveal on scroll --------------------------------------------------------
    const reveals = document.querySelectorAll('[data-sk-reveal]');
    if (hasGSAP && hasST) {
      reveals.forEach((el) => {
        // Product cards and their wrappers get a pure fade-in (no Y translate)
        // because the translate motion combined with the emerald border's
        // transition caused a "shaky pop" on catalog grids.
        const isCard = el.matches('.sk-card, [data-sk-card]') || el.querySelector('.sk-card, [data-sk-card]');
        gsap.fromTo(
          el,
          { opacity: 0, y: isCard ? 0 : 18 },
          {
            opacity: 1,
            y: 0,
            duration: isCard ? 0.45 : 0.7,
            ease: 'power2.out',
            scrollTrigger: { trigger: el, start: 'top 92%', toggleActions: 'play none none none' },
            onStart: () => el.classList.add('is-visible')
          }
        );
      });
    } else if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -10% 0px' });
      reveals.forEach((el) => io.observe(el));
    } else {
      reveals.forEach((el) => el.classList.add('is-visible'));
    }

    // Hero parallax -----------------------------------------------------------
    const heroMedia = document.querySelector('.sk-hero__media');
    if (heroMedia && hasGSAP && hasST) {
      gsap.to(heroMedia, {
        yPercent: 14,
        ease: 'none',
        scrollTrigger: { trigger: heroMedia, start: 'top top', end: 'bottom top', scrub: true }
      });
    }

    // Header scroll state + body scrolled class -------------------------------
    const header = document.querySelector('.sk-header');
    const body = document.body;
    const update = () => {
      if (header) header.classList.toggle('is-scrolled', window.scrollY > 16);
      body.classList.toggle('sk-scrolled', window.scrollY > 400);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });

    // Lightweight card parallax on fine pointers ------------------------------
    if (matchMedia('(pointer: fine)').matches) {
      document.querySelectorAll('[data-sk-card]').forEach((card) => {
        const media = card.querySelector('.sk-card__media');
        if (!media) return;
        let rect;
        card.addEventListener('mouseenter', () => { rect = card.getBoundingClientRect(); });
        card.addEventListener('mousemove', (e) => {
          if (!rect) rect = card.getBoundingClientRect();
          const x = (e.clientX - rect.left) / rect.width - 0.5;
          const y = (e.clientY - rect.top) / rect.height - 0.5;
          media.style.transform = `translate3d(${x * 4}px, ${y * 4}px, 0)`;
        });
        card.addEventListener('mouseleave', () => { media.style.transform = ''; rect = null; });
      });
    }
  }

  ready(init);

  // Re-run reveal scanning when the Shopify theme editor swaps a section.
  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('[data-sk-reveal]:not(.is-visible)').forEach((el) => el.classList.add('is-visible'));
  });
})(typeof window !== 'undefined' ? window : this);
